import { baseApi } from '../../services/baseApi';
import type { Binding } from '../../pages/admin/platform/mockData';

/* ------------------------------------------------------------------ *
 * Platform Admin API (branch: platformAdmin), under /platform/.
 * Covers all six phases: templates, states + onboarding, state profiles,
 * territories + units, people (post holders), and the review queue.
 * ------------------------------------------------------------------ */

const P = 'platform';
const st = (code: string) => `${P}/states/${encodeURIComponent(code)}`;

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

/* ---- Onboarding Step 1: import standard sources ---- */
export interface ImportSourceStatus {
  source: 'ECI' | 'SEC' | 'LGD';
  status: 'notstarted' | 'running' | 'done' | 'error';
  filename?: string | null;
  message?: string | null;
  detail?: Record<string, unknown> | null;
  updated_at?: string | null;
}
export interface EciDetail {
  /** The resolved ECI state (null when the platform state isn't in ECI data). */
  state_id?: number | null;
  state_code?: string | null;
  state_name?: string | null;
  district?: number;
  parliament: number;
  assembly: number;
  booths: number;
  assemblies: { ac_no: number; name: string; pc: string | null; booths: number }[];
}

/* ---- Phase 1: templates ---- */
export interface TemplateListItem {
  code: string;
  name: string;
  control: string;
  bottom_unit: string;
  note?: string;
  level_count: number;
}
export interface TemplateLevel {
  position: number;
  name: string;
  binding: Binding;
  binding_label: string;
  official_post: string;
  elected_post: string;
}
export interface TemplateDetail extends TemplateListItem {
  counts: { anchors: number; inherited: number; own: number };
  levels: TemplateLevel[];
}

/* ---- Phase 2: states + onboarding ---- */
export type StateStatus = 'notstarted' | 'onboarding' | 'live' | 'paused';
export interface CatalogState {
  code: string;
  name: string;
  language: string;
}
export interface PlatformStateRow {
  code: string;
  name: string;
  language: string;
  status: StateStatus;
  clients: number;
  steps_done: number;
  progress: number;
  /** ECI state id — the value a parliament row's `state` FK holds (e.g. 29 for Rajasthan). */
  state_id?: number;
  /** ECI state code (e.g. "S20" for Rajasthan). */
  state_code?: string;
  created_at?: string;
  updated_at?: string;
}
export interface OnboardingStep {
  step: number;
  title: string;
  desc: string;
  action: string;
  done: boolean;
  current: boolean;
}
export interface OnboardingResponse {
  state: string;
  code: string;
  status: StateStatus;
  steps_done: number;
  steps: OnboardingStep[];
}

/* ---- Phase 3: state departments ---- */
export interface StateDepartmentRow {
  id: number;
  code: string;
  name: string;
  control: string;
  bottom_unit: string;
  customised: boolean;
  reviewed: boolean;
  activated?: boolean;
  coverage: number | null;
  minister: string | null;
  level_count: number;
}
export interface StateDeptLevel {
  position: number;
  name: string;
  binding: Binding;
  binding_label: string;
  official_post: string;
  elected_post: string;
  /** The source the level's nodes come from: LGD | ECI | SEC | user. */
  source?: string;
  /** The specific source level this level mirrors (e.g. "Parliament"), when known. */
  source_level?: string;
  /** Real "in boundary" count: Own = hand-entered units; Anchored/Inherited = source-node count. */
  unit_count?: number;
}
export interface StateDepartmentDetail extends StateDepartmentRow {
  levels_source: 'template' | 'own';
  levels: StateDeptLevel[];
}
export interface StateDeptLevelInput {
  name: string;
  binding: Binding;
  official_post?: string;
  elected_post?: string;
  /** Source system (LGD | ECI | SEC | user) an Anchored level references. */
  source?: string;
  /** The specific source level this level mirrors (e.g. "Parliament"). */
  source_level?: string;
}
/** POST body: either enable a standard template, or add a state-only hierarchy. */
export type EnableDepartmentBody =
  | { template_code: string }
  | { code: string; name: string; control: string; levels: StateDeptLevelInput[] };
/** PATCH body: update meta, customise levels, or reset to template. */
export type UpdateDepartmentBody =
  | { reviewed?: boolean; activated?: boolean; coverage?: number; minister?: string; name?: string; control?: string }
  | { levels: StateDeptLevelInput[] }
  | { reset: true };

/**
 * Client Admin context — the logged-in client's organization + its state, derived on the backend
 * from the user's org (org.state_code). No state code is passed by the frontend.
 */
export interface ClientContext {
  organization_id: number;
  organization_name: string;
  state_code: string;
  state_name: string;
}
/** A member of the logged-in client's team (org user + roles). */
export interface ClientTeamMember {
  id: number;
  name: string | null;
  email: string | null;
  directory: string | null;
  designation: string | string[] | null;
  location: number | null;
  roles: string[];
}
/** A role + its permission actions (the team permissions matrix). */
export interface ClientRole {
  id: number;
  name: string;
  description: string | null;
  permissions: string[];
}
/** The client's party org hierarchy (levels + nested node tree). */
export interface ClientPartyNode {
  id: number;
  org_name?: string | null;
  level_name?: string | null;
  label?: string | null;
  children?: ClientPartyNode[];
  [k: string]: unknown;
}
export interface ClientParty {
  has_hierarchy: boolean;
  levels: { id: number; level_name?: string; position?: number; [k: string]: unknown }[];
  tree: ClientPartyNode[];
}
/** One coverage gap across the client's state departments. */
export interface ClientGap {
  dept: string;
  template_code: string;
  level: string;
  coverage: number | null;
  message: string;
}

/* ---- Phase 4: territories + units ---- */
export type Nesting = 'within' | 'derive' | 'free';
export interface TerritoryRow {
  dept: string;
  template_code: string;
  level: string;
  binding: Binding;
  binding_label: string;
  units: number;
  coverage: number | null;
  nesting: Nesting | null;
}
export interface Unit {
  id: number;
  name: string;
  code: string | null;
  parent: number | null;
  level_name: string;
  template_code: string;
  state_code: string;
  /** Mapped-member sub-counts (own levels): total + grouped by node level. */
  member_count?: number;
  members_by_level?: Record<string, number>;
}
export interface UnitsResponse {
  template_code: string;
  level: string;
  nesting: Nesting | null;
  coverage: number | null;
  units: Unit[];
}

/** Result of a bulk units import (POST .../units/import/). */
export interface UnitsImportResult {
  template_code: string;
  level: string;
  rows: number;
  units_created: number;
  units_existing: number;
  members_added: number;
  unmatched: { row: number; unit: string; member_type: string; value: string; reason: string }[];
}

/** Government editor payload: the structure JSON + a department code→name map (display-only). */
export interface GovernmentResponse {
  data: Record<string, unknown>;
  departments: Record<string, string>;
}

/** Result of pushing a department's own-listed units into the Resource directory (Draft). */
export interface UnitsToResourcesResult {
  created: number;
  existing: number;
  total: number;
  levels: string[];
}

/** An LGD/ECI/SEC node that an Own-Grouped unit can be composed of. */
export interface AvailableNode {
  source: string;
  node_level: string;
  node_code: string;
  node_name: string;
}
export interface UnitMember extends AvailableNode {
  id?: number;
}

/**
 * The real source nodes that make up an Anchored/Inherited level — resolved by the level's
 * MAPPED source_level (e.g. District → ECI districts, Tehsil inheriting ECI › Booth → booths).
 * Read-only: GET /platform/states/<code>/level-nodes/?template_code=&level=(&search=).
 */
export interface LevelNodesResponse {
  binding?: string;
  source?: string;
  source_level?: string;
  node_level?: string;
  nodes: AvailableNode[];
}

/** A source level (from imported LGD/ECI/SEC data) an Anchored level can point at. */
export interface AnchorLevel {
  source: string;
  level: string;
  label: string;
}

/** A binding type + its human explanation (GET /platform/bindings/) — the single source of truth. */
export interface BindingOption {
  value: string;
  label: string;
  description?: string;
}

/** Territories "Validation" panel: own-level gaps + notes + activation gate. */
export interface ValidationGap {
  level: string;
  coverage: number | null;
  gap_pct: number | null;
  message: string;
}
export interface ValidationResponse {
  gaps: ValidationGap[];
  info: string[];
  can_activate: boolean;
}

/** Territories "Village lookup" panel: a village's standard path + which own units hold it. */
export interface VillageLookup {
  village: { code: string; name: string } | null;
  standard: {
    village?: string;
    gram_panchayat?: string;
    block?: string;
    assembly?: string;
    parliament?: string;
    district?: string;
    state?: string;
  };
  custom: { template_code: string; level: string; unit: string; unit_id: number }[];
}

/** Department-scoped government: the minister(s) holding this department + committees covering it. */
export interface DeptGovMinister {
  /** Post title (e.g. "Cabinet Minister") — the API's primary label for the minister row. */
  title?: string;
  designation?: string;
  person?: string;
  name?: string;
  kind?: string;
  order?: number;
  /** Who this minister reports to (shown as a sub-line); derived from order when absent. */
  reports_to?: string;
}
export interface DeptGovCommitteeMember {
  person?: string;
  name?: string;
  role?: string;
  house?: string;
}
export interface DeptGovCommittee {
  name?: string;
  type?: string;
  servicing?: string;
  members?: DeptGovCommitteeMember[];
}
export interface DeptGovernment {
  ministers?: DeptGovMinister[];
  /** Some responses use `positions` for the minister list. */
  positions?: DeptGovMinister[];
  committees?: DeptGovCommittee[];
}

/** Resolve one node's ancestry across the standard LGD/ECI/SEC sources. */
export interface AreaInfo {
  level: string;
  code: string;
  name: string;
  gram_panchayat?: { name: string; lgd_code?: string } | null;
  block?: { name: string; code?: string } | null;
  assembly?: { name: string; ac_code?: string } | null;
  parliament?: { name: string; pc_code?: string } | null;
  district?: { name: string } | null;
  state?: { name: string } | null;
}

/* ---- Phase 5: people / post holders ---- */
export interface PostLadderRow {
  level: string;
  rank: number;
  designation: string;
  type: 'official' | 'elected';
  units: number;
  recorded: number;
}
export interface PostHolder {
  id: number;
  template_code: string;
  level_name: string;
  designation: string;
  type: 'official' | 'elected';
  unit: number | null;
  unit_name: string | null;
  person_name: string;
  source: string | null;
  record_date: string | null;
  created_at?: string;
}

/* ---- Phase 6: review queue ---- */
export type ReviewStatus = 'pending' | 'approved' | 'rejected';
export interface ReviewItem {
  id: number;
  state_code: string;
  type: string;
  title: string;
  detail: string;
  effect: string;
  from_client: string;
  organization: number | null;
  status: ReviewStatus;
  created_at?: string;
  decided_at?: string | null;
}

export const platformApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /* ---------------- Phase 1: templates ---------------- */
    getTemplates: build.query<TemplateListItem[], void>({
      query: () => ({ url: `${P}/templates/` }),
      transformResponse: (r: Listish<TemplateListItem>) => asList(r),
      providesTags: ['PlatformTemplates'],
    }),
    getTemplate: build.query<TemplateDetail, string>({
      query: (code) => ({ url: `${P}/templates/${encodeURIComponent(code)}/` }),
      providesTags: ['PlatformTemplates'],
    }),

    /* ---------------- Phase 2: states + onboarding ---------------- */
    getStatesCatalog: build.query<CatalogState[], void>({
      query: () => ({ url: `${P}/states/catalog/` }),
      transformResponse: (r: Listish<CatalogState>) => asList(r),
    }),
    getStates: build.query<PlatformStateRow[], void>({
      query: () => ({ url: `${P}/states/` }),
      transformResponse: (r: Listish<PlatformStateRow>) => asList(r),
      providesTags: ['PlatformStates'],
    }),
    getState: build.query<PlatformStateRow, string>({
      query: (code) => ({ url: `${st(code)}/` }),
      providesTags: ['PlatformStates'],
    }),
    createState: build.mutation<PlatformStateRow, { code: string }>({
      // Send both `code` and `state_code` so the backend gets the state key either way.
      query: ({ code }) => ({ url: `${P}/states/`, method: 'POST', body: { code, state_code: code } }),
      invalidatesTags: ['PlatformStates'],
    }),
    updateState: build.mutation<
      PlatformStateRow,
      { code: string; patch: Partial<Pick<PlatformStateRow, 'status' | 'steps_done' | 'clients' | 'progress'>> }
    >({
      query: ({ code, patch }) => ({ url: `${st(code)}/`, method: 'PATCH', body: patch }),
      invalidatesTags: ['PlatformStates', 'PlatformOnboarding'],
    }),
    deleteState: build.mutation<void, string>({
      query: (code) => ({ url: `${st(code)}/`, method: 'DELETE' }),
      invalidatesTags: ['PlatformStates'],
    }),
    getOnboarding: build.query<OnboardingResponse, string>({
      query: (code) => ({ url: `${st(code)}/onboarding/` }),
      providesTags: ['PlatformOnboarding'],
    }),

    /* ---------------- Phase 3: state departments ---------------- */
    getStateDepartments: build.query<StateDepartmentRow[], string>({
      query: (code) => ({ url: `${st(code)}/departments/` }),
      transformResponse: (r: Listish<StateDepartmentRow>) => asList(r),
      providesTags: ['PlatformStateDepartments'],
    }),
    getStateDepartment: build.query<StateDepartmentDetail, { code: string; tcode: string }>({
      query: ({ code, tcode }) => ({ url: `${st(code)}/departments/${encodeURIComponent(tcode)}/` }),
      providesTags: ['PlatformStateDepartments'],
    }),
    enableDepartment: build.mutation<StateDepartmentDetail, { code: string; body: EnableDepartmentBody }>({
      query: ({ code, body }) => ({ url: `${st(code)}/departments/`, method: 'POST', body }),
      invalidatesTags: ['PlatformStateDepartments', 'PlatformTerritories'],
    }),
    updateStateDepartment: build.mutation<
      StateDepartmentDetail,
      { code: string; tcode: string; patch: UpdateDepartmentBody }
    >({
      query: ({ code, tcode, patch }) => ({
        url: `${st(code)}/departments/${encodeURIComponent(tcode)}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['PlatformStateDepartments', 'PlatformTerritories', 'PlatformPeople'],
    }),
    deleteStateDepartment: build.mutation<void, { code: string; tcode: string }>({
      query: ({ code, tcode }) => ({
        url: `${st(code)}/departments/${encodeURIComponent(tcode)}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PlatformStateDepartments', 'PlatformTerritories'],
    }),

    /* ---------------- Client Admin (scoped to the logged-in client's state) ---------------- */
    // The client's org + state, derived on the backend from the user's org — no code passed.
    getClientContext: build.query<ClientContext, void>({
      query: () => ({ url: `${P}/client/context/` }),
      providesTags: ['PlatformStates'],
    }),
    getClientDepartments: build.query<StateDepartmentRow[], void>({
      query: () => ({ url: `${P}/client/departments/` }),
      transformResponse: (r: Listish<StateDepartmentRow>) => asList(r),
      providesTags: ['PlatformStateDepartments'],
    }),
    getClientTerritories: build.query<TerritoryRow[], void>({
      query: () => ({ url: `${P}/client/territories/` }),
      transformResponse: (r: Listish<TerritoryRow>) => asList(r),
      providesTags: ['PlatformTerritories'],
    }),
    getClientTeam: build.query<ClientTeamMember[], void>({
      query: () => ({ url: `${P}/client/team/` }),
      transformResponse: (r: Listish<ClientTeamMember>) => asList(r),
      providesTags: ['PlatformPeople'],
    }),
    getClientRoles: build.query<ClientRole[], void>({
      query: () => ({ url: `${P}/client/roles/` }),
      transformResponse: (r: Listish<ClientRole>) => asList(r),
      providesTags: ['Roles'],
    }),
    getClientParty: build.query<ClientParty, void>({
      query: () => ({ url: `${P}/client/party/` }),
      providesTags: ['OrgNode'],
    }),
    getClientGaps: build.query<ClientGap[], void>({
      query: () => ({ url: `${P}/client/gaps/` }),
      transformResponse: (r: { gaps?: ClientGap[] } | ClientGap[] | null) =>
        Array.isArray(r) ? r : (r?.gaps ?? []),
      providesTags: ['PlatformTerritories'],
    }),
    submitClientGap: build.mutation<unknown, { title: string; type?: string; detail?: string }>({
      query: (body) => ({ url: `${P}/client/gaps/`, method: 'POST', body }),
      invalidatesTags: ['PlatformReviewQueue'],
    }),

    /* ---------------- Phase 4: territories + units ---------------- */
    getTerritories: build.query<TerritoryRow[], string>({
      query: (code) => ({ url: `${st(code)}/territories/` }),
      transformResponse: (r: Listish<TerritoryRow>) => asList(r),
      providesTags: ['PlatformTerritories'],
    }),
    getUnits: build.query<UnitsResponse, { code: string; template_code: string; level: string }>({
      query: ({ code, template_code, level }) => ({
        url: `${st(code)}/units/`,
        params: { template_code, level },
      }),
      providesTags: ['PlatformUnits'],
    }),
    createUnit: build.mutation<
      Unit,
      { code: string; template_code: string; level: string; name: string; unitCode?: string; parent?: number }
    >({
      query: ({ code, template_code, level, name, unitCode, parent }) => ({
        url: `${st(code)}/units/`,
        method: 'POST',
        body: { template_code, level, name, ...(unitCode ? { code: unitCode } : {}), ...(parent != null ? { parent } : {}) },
      }),
      invalidatesTags: ['PlatformUnits', 'PlatformTerritories'],
    }),
    setLevelRule: build.mutation<
      UnitsResponse,
      { code: string; template_code: string; level: string; nesting?: Nesting; coverage?: number }
    >({
      query: ({ code, template_code, level, nesting, coverage }) => ({
        url: `${st(code)}/units/`,
        method: 'PATCH',
        body: { template_code, level, ...(nesting ? { nesting } : {}), ...(coverage != null ? { coverage } : {}) },
      }),
      invalidatesTags: ['PlatformUnits', 'PlatformTerritories'],
    }),
    updateUnit: build.mutation<
      Unit,
      { code: string; id: number; patch: { name?: string; code?: string; parent?: number } }
    >({
      query: ({ code, id, patch }) => ({ url: `${st(code)}/units/${id}/`, method: 'PATCH', body: patch }),
      invalidatesTags: ['PlatformUnits', 'PlatformTerritories'],
    }),
    deleteUnit: build.mutation<void, { code: string; id: number }>({
      query: ({ code, id }) => ({ url: `${st(code)}/units/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['PlatformUnits', 'PlatformTerritories'],
    }),
    // Bulk import units + members from a CSV/XLSX (multipart).
    importUnits: build.mutation<
      UnitsImportResult,
      { code: string; template_code: string; level: string; file: File }
    >({
      query: ({ code, template_code, level, file }) => {
        const form = new FormData();
        form.append('file', file);
        form.append('template_code', template_code);
        form.append('level', level);
        return { url: `${st(code)}/units/import/`, method: 'POST', body: form };
      },
      invalidatesTags: ['PlatformUnits', 'PlatformTerritories', 'PlatformUnitMembers'],
    }),
    // Push a department's own-listed units (PHC, school, …) into the Resource directory as Draft
    // resources. `level` optional — omit to export ALL own-listed levels of the department.
    // Idempotent: only new units are added (existing ones are skipped, no duplicates).
    addUnitsToResources: build.mutation<
      UnitsToResourcesResult,
      { code: string; template_code: string; level?: string }
    >({
      query: ({ code, template_code, level }) => ({
        url: `${st(code)}/units/to-resources/`,
        method: 'POST',
        body: { template_code, ...(level ? { level } : {}) },
      }),
      invalidatesTags: ['Resources'],
    }),

    /* ---- Own-Grouped node mapping: compose a unit from LGD/ECI/SEC nodes ---- */
    getAvailableNodes: build.query<
      AvailableNode[],
      {
        source: string;
        level: string;
        search?: string;
        state?: string;
        /** Hide nodes already taken by another unit of (state, template_code, unit_level). */
        onlyUnassigned?: boolean;
        template_code?: string;
        unitLevel?: string;
        excludeUnit?: number;
      }
    >({
      query: ({ source, level, search, state, onlyUnassigned, template_code, unitLevel, excludeUnit }) => ({
        url: `${P}/available-nodes/`,
        params: {
          source,
          level,
          ...(search ? { search } : {}),
          ...(state ? { state } : {}),
          ...(onlyUnassigned
            ? {
                only_unassigned: 1,
                ...(template_code ? { template_code } : {}),
                ...(unitLevel ? { unit_level: unitLevel } : {}),
                ...(excludeUnit != null ? { exclude_unit: excludeUnit } : {}),
              }
            : {}),
        },
      }),
      transformResponse: (r: Listish<AvailableNode>) => asList(r),
      providesTags: ['PlatformUnitMembers'],
    }),
    // The real source nodes of an Anchored/Inherited level (read-only), resolved by its mapped
    // source_level — instead of guessing from the level name.
    getLevelNodes: build.query<
      LevelNodesResponse,
      {
        code: string;
        template_code: string;
        level: string;
        search?: string;
        /** Hide nodes/units already taken by another unit of `unitLevel` (needs excludeUnit). */
        onlyUnassigned?: boolean;
        /** The parent Own level whose units compete for this pool. */
        unitLevel?: string;
        /** The current unit — its own members stay visible. */
        excludeUnit?: number;
      }
    >({
      query: ({ code, template_code, level, search, onlyUnassigned, unitLevel, excludeUnit }) => ({
        url: `${st(code)}/level-nodes/`,
        params: {
          template_code,
          level,
          ...(search ? { search } : {}),
          ...(onlyUnassigned ? { only_unassigned: 1 } : {}),
          ...(unitLevel ? { unit_level: unitLevel } : {}),
          ...(excludeUnit != null ? { exclude_unit: excludeUnit } : {}),
        },
      }),
      transformResponse: (r: Partial<LevelNodesResponse> | null): LevelNodesResponse => ({
        binding: r?.binding,
        source: r?.source,
        source_level: r?.source_level,
        node_level: r?.node_level,
        nodes: Array.isArray(r?.nodes) ? (r!.nodes as AvailableNode[]) : [],
      }),
      providesTags: ['PlatformUnitMembers'],
    }),
    getUnitMembers: build.query<UnitMember[], { code: string; unitId: number }>({
      query: ({ code, unitId }) => ({ url: `${st(code)}/units/${unitId}/members/` }),
      transformResponse: (r: Listish<UnitMember>) => asList(r),
      providesTags: ['PlatformUnitMembers'],
    }),
    saveUnitMembers: build.mutation<UnitMember[], { code: string; unitId: number; nodes: AvailableNode[] }>({
      query: ({ code, unitId, nodes }) => ({
        url: `${st(code)}/units/${unitId}/members/`,
        method: 'PUT',
        body: { nodes },
      }),
      transformResponse: (r: Listish<UnitMember>) => asList(r),
      invalidatesTags: ['PlatformUnitMembers', 'PlatformTerritories'],
    }),

    /* ---- Department-scoped government: minister(s) + committees for one department ---- */
    getDepartmentGovernment: build.query<DeptGovernment, { code: string; tcode: string }>({
      query: ({ code, tcode }) => ({ url: `${st(code)}/departments/${encodeURIComponent(tcode)}/government/` }),
      transformResponse: (r: ({ data?: DeptGovernment } & DeptGovernment) | null): DeptGovernment =>
        (r && r.data ? r.data : r) ?? {},
      providesTags: ['PlatformGovernment'],
    }),

    /* ---- Anchor levels: source levels (imported LGD/ECI/SEC) an anchor can reference ---- */
    getAnchorLevels: build.query<AnchorLevel[], string>({
      query: (code) => ({ url: `${st(code)}/anchor-levels/` }),
      transformResponse: (r: Listish<AnchorLevel>) => asList(r),
    }),

    /* ---- Binding types + their descriptions (single source of truth for the help text) ---- */
    getBindings: build.query<BindingOption[], void>({
      query: () => ({ url: `${P}/bindings/` }),
      transformResponse: (r: Listish<BindingOption>) => asList(r),
    }),

    /* ---- Territories: validation / gaps for a department ---- */
    getValidation: build.query<ValidationResponse, { code: string; template_code: string }>({
      query: ({ code, template_code }) => ({ url: `${st(code)}/validation/`, params: { template_code } }),
      providesTags: ['PlatformTerritories'],
    }),

    /* ---- Territories: village lookup (standard path + custom units) ---- */
    getVillageLookup: build.query<VillageLookup, { code: string; village?: string; department?: string }>({
      query: ({ code, village, department }) => ({
        url: `${st(code)}/village-lookup/`,
        params: { ...(village ? { village } : {}), ...(department ? { department } : {}) },
      }),
      providesTags: ['PlatformTerritories'],
    }),

    /* ---- Area info: resolve a node's ancestry across LGD/ECI/SEC sources ---- */
    getAreaInfo: build.query<AreaInfo, { level: string; code: string }>({
      query: ({ level, code }) => ({ url: `territory/area-info/`, params: { level, code } }),
    }),

    /* ---------------- Phase 5: people / post holders ---------------- */
    getPeople: build.query<PostLadderRow[], { code: string; template_code: string }>({
      query: ({ code, template_code }) => ({ url: `${st(code)}/people/`, params: { template_code } }),
      transformResponse: (r: Listish<PostLadderRow>) => asList(r),
      providesTags: ['PlatformPeople'],
    }),
    getPostHolders: build.query<
      PostHolder[],
      { code: string; template_code: string; level?: string; designation?: string }
    >({
      query: ({ code, template_code, level, designation }) => ({
        url: `${st(code)}/post-holders/`,
        params: { template_code, ...(level ? { level } : {}), ...(designation ? { designation } : {}) },
      }),
      transformResponse: (r: Listish<PostHolder>) => asList(r),
      providesTags: ['PlatformPostHolders'],
    }),
    createPostHolder: build.mutation<
      PostHolder,
      {
        code: string;
        template_code: string;
        level: string;
        designation: string;
        type: 'official' | 'elected';
        person_name: string;
        unit?: number;
        unit_name?: string;
        source?: string;
        record_date?: string;
      }
    >({
      query: ({ code, ...body }) => ({ url: `${st(code)}/post-holders/`, method: 'POST', body }),
      invalidatesTags: ['PlatformPostHolders', 'PlatformPeople'],
    }),
    updatePostHolder: build.mutation<
      PostHolder,
      {
        code: string;
        id: number;
        patch: { person_name?: string; source?: string; record_date?: string; unit?: number; unit_name?: string };
      }
    >({
      query: ({ code, id, patch }) => ({ url: `${st(code)}/post-holders/${id}/`, method: 'PATCH', body: patch }),
      invalidatesTags: ['PlatformPostHolders', 'PlatformPeople'],
    }),
    deletePostHolder: build.mutation<void, { code: string; id: number }>({
      query: ({ code, id }) => ({ url: `${st(code)}/post-holders/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['PlatformPostHolders', 'PlatformPeople'],
    }),

    /* ---------------- Phase 6: review queue ---------------- */
    getReviewQueue: build.query<ReviewItem[], { code: string; status?: ReviewStatus }>({
      query: ({ code, status }) => ({ url: `${st(code)}/review-queue/`, params: status ? { status } : {} }),
      transformResponse: (r: Listish<ReviewItem>) => asList(r),
      providesTags: ['PlatformReviewQueue'],
    }),
    createReviewItem: build.mutation<
      ReviewItem,
      {
        code: string;
        type: string;
        title: string;
        detail?: string;
        effect?: string;
        from_client?: string;
        organization?: number;
      }
    >({
      query: ({ code, ...body }) => ({ url: `${st(code)}/review-queue/`, method: 'POST', body }),
      invalidatesTags: ['PlatformReviewQueue'],
    }),
    getReviewItem: build.query<ReviewItem, { code: string; id: number }>({
      query: ({ code, id }) => ({ url: `${st(code)}/review-queue/${id}/` }),
      providesTags: ['PlatformReviewQueue'],
    }),
    updateReviewItem: build.mutation<
      ReviewItem,
      { code: string; id: number; status: 'approved' | 'rejected' }
    >({
      query: ({ code, id, status }) => ({
        url: `${st(code)}/review-queue/${id}/`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['PlatformReviewQueue'],
    }),

    /* ---- Government editor: load/save the whole structure per state ---- */
    // Returns the structure (`data`) plus a department code→name map (`departments`) used to
    // render the minister table's Departments column (depts are stored as codes).
    getGovernment: build.query<GovernmentResponse, string>({
      query: (code) => ({ url: `${st(code)}/government/` }),
      transformResponse: (
        r: { data?: Record<string, unknown>; departments?: Record<string, string> } | null,
      ) => ({ data: r?.data ?? {}, departments: r?.departments ?? {} }),
      providesTags: ['PlatformGovernment'],
    }),
    saveGovernment: build.mutation<Record<string, unknown>, { code: string; data: unknown }>({
      query: ({ code, data }) => ({ url: `${st(code)}/government/`, method: 'PUT', body: { data } }),
      transformResponse: (r: { data?: Record<string, unknown> } | null) => r?.data ?? {},
    }),

    /* ---- Onboarding Step 1: import standard sources (ECI / SEC / LGD) ---- */
    getImportStatus: build.query<ImportSourceStatus[], string>({
      query: (code) => ({ url: `${st(code)}/import/` }),
      transformResponse: (r: Listish<ImportSourceStatus>) => asList(r),
      providesTags: ['PlatformImport'],
    }),
    // mark a source done (used for ECI after the /access/data-sync/ pull finishes)
    setImportStatus: build.mutation<
      ImportSourceStatus,
      { code: string; source: 'ECI' | 'SEC' | 'LGD'; status: string; message?: string }
    >({
      query: ({ code, ...body }) => ({ url: `${st(code)}/import/`, method: 'PATCH', body }),
      invalidatesTags: ['PlatformImport', 'PlatformOnboarding'],
    }),
    // SEC: upload the PRI/ULB Excel (multipart; field 'file')
    importSec: build.mutation<ImportSourceStatus, { code: string; file: File }>({
      query: ({ code, file }) => {
        const form = new FormData();
        form.append('file', file);
        return { url: `${st(code)}/import/sec/`, method: 'POST', body: form };
      },
      invalidatesTags: ['PlatformImport', 'PlatformOnboarding'],
    }),
    // LGD: upload for now (auto-download from lgdirectory.gov.in later)
    importLgd: build.mutation<ImportSourceStatus, { code: string; file: File }>({
      query: ({ code, file }) => {
        const form = new FormData();
        form.append('file', file);
        return { url: `${st(code)}/import/lgd/`, method: 'POST', body: form };
      },
      invalidatesTags: ['PlatformImport', 'PlatformOnboarding'],
    }),
    // ECI drill-in: PCs / ACs / booths for the state
    getEciDetail: build.query<EciDetail, string>({
      query: (code) => ({ url: `${st(code)}/import/eci/detail/` }),
    }),
    // SEC: download the PRI/ULB Excel — blank template, or ?filled with DB data
    getSecTemplate: build.query<Blob, { filled?: boolean } | void>({
      query: (arg) => ({
        url: `${P}/import/sec-template/`,
        params: arg && arg.filled ? { filled: 1 } : {},
        responseHandler: (response) => response.blob(),
        cache: 'no-cache',
      }),
    }),
    // Units import: download the .xlsx template (member_type pre-filled when template_code+level given)
    getUnitsTemplate: build.query<Blob, { template_code?: string; level?: string; state?: string }>({
      query: ({ template_code, level, state }) => ({
        url: `${P}/import/units-template/`,
        params: {
          ...(template_code ? { template_code } : {}),
          ...(level ? { level } : {}),
          ...(state ? { state } : {}),
        },
        responseHandler: (response) => response.blob(),
        cache: 'no-cache',
      }),
    }),
  }),
});

export const {
  // Phase 1
  useGetTemplatesQuery,
  useGetTemplateQuery,
  // Phase 2
  useGetStatesCatalogQuery,
  useGetStatesQuery,
  useGetStateQuery,
  useCreateStateMutation,
  useUpdateStateMutation,
  useDeleteStateMutation,
  useGetOnboardingQuery,
  // Phase 3
  useGetStateDepartmentsQuery,
  useGetStateDepartmentQuery,
  useEnableDepartmentMutation,
  useUpdateStateDepartmentMutation,
  useDeleteStateDepartmentMutation,
  // Client Admin (scoped to the logged-in client's state)
  useGetClientContextQuery,
  useGetClientDepartmentsQuery,
  useGetClientTerritoriesQuery,
  useGetClientTeamQuery,
  useGetClientRolesQuery,
  useGetClientPartyQuery,
  useGetClientGapsQuery,
  useSubmitClientGapMutation,
  // Phase 4
  useGetTerritoriesQuery,
  useGetUnitsQuery,
  useCreateUnitMutation,
  useSetLevelRuleMutation,
  useUpdateUnitMutation,
  useDeleteUnitMutation,
  useImportUnitsMutation,
  useAddUnitsToResourcesMutation,
  useGetAvailableNodesQuery,
  useGetLevelNodesQuery,
  useGetUnitMembersQuery,
  useSaveUnitMembersMutation,
  useGetAnchorLevelsQuery,
  useGetBindingsQuery,
  useGetValidationQuery,
  useGetVillageLookupQuery,
  useGetDepartmentGovernmentQuery,
  useGetAreaInfoQuery,
  // Phase 5
  useGetPeopleQuery,
  useGetPostHoldersQuery,
  useCreatePostHolderMutation,
  useUpdatePostHolderMutation,
  useDeletePostHolderMutation,
  // Phase 6
  useGetReviewQueueQuery,
  useCreateReviewItemMutation,
  useGetReviewItemQuery,
  useUpdateReviewItemMutation,
  // Government editor
  useGetGovernmentQuery,
  useSaveGovernmentMutation,
  // Onboarding Step 1 — import sources
  useGetImportStatusQuery,
  useSetImportStatusMutation,
  useImportSecMutation,
  useImportLgdMutation,
  useGetEciDetailQuery,
  useLazyGetSecTemplateQuery,
  useLazyGetUnitsTemplateQuery,
} = platformApi;
